import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { gatewayStatusAction } from "../Admin/Store/admin.action";
import { commonStyles } from "../../../assets/MUIStyles/commonStyles";
import moment from "moment";
import { dashboardStyles } from "../../../assets/MUIStyles/dashboard";
import { ReactComponent as RefreshIcon } from "../../../assets/icons/refresh.svg";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";

const getStatusColor = (status: number) => {
  switch (status) {
    case 200:
      return { color: "#e57373", bg: "#fdecea" };
    case 404:
      return { color: "#6d4c41", bg: "#efebe9" };
    case 479:
      return { color: "#673ab7", bg: "#ede7f6" };
    case 480:
      return { color: "#ba68c8", bg: "#f3e5f5" };
    case 486:
      return { color: "#7986cb", bg: "#e8eaf6" };
    default:
      return { color: "#90a4ae", bg: "#eceff1" };
  }
};

const GatewayDetails = () => {
  const dispatch = useDispatch();
  const reducer: any = useSelector((state) => state);

  const {
    AccountReducer: { constantData },
  } = reducer;

  const [gatewayData, setGatewayData] = useState([]);
  const [dispositions, setDispositions] = useState<any>([]);
  const [selectedGateway, setSelectedGateway] = useState<any>({});
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [selectedDate, setSelectedDate] = useState(moment().toDate());
  const [totalCalls, setTotalCalls] = useState(0);

  useEffect(() => {
    const init = async () => {
      const res: any = await dispatch(
        gatewayStatusAction({
          date: moment(selectedDate).format("DD-MM-YYYY"),
        })
      );
      setGatewayData(res);

      if (constantData?.gateways?.length > 0) {
        const defaultGateway =
          constantData?.gateways?.find((g) => g?.default) ||
          constantData?.gateways[0];

        setSelectedGateway(defaultGateway);
        setDispositionsData(res, defaultGateway);
      }
    };
    init();
  }, [constantData]);

  const setDispositionsData = (data, gateway) => {
    const matched = data?.find((g) => g?.gateway_name === gateway?.value);
    let total = 0;

    if (matched?.disposition) {
      const formatted = matched?.disposition?.map((item) => {
        const { color, bg } = getStatusColor(Number(item?.code));
        return {
          status: item?.code,
          disposition: item?.status,
          calls: item?.count,
          percent: item?.percentage,
          color,
          bg,
        };
      });
      for (const data of matched?.disposition) {
        total += data?.count;
      }
      setTotalCalls(total);
      setDispositions(formatted);
    } else {
      setTotalCalls(0);
      setDispositions([]);
    }
  };

  const applyRefresh = async () => {
    setRefreshLoading(true);
    const res: any = await dispatch(
      gatewayStatusAction({
        date: moment(selectedDate).format("DD-MM-YYYY"),
      })
    );
    setGatewayData(res);
    setDispositionsData(res, selectedGateway);
    setRefreshLoading(false);
  };

  const handleChange = (selected: any) => {
    setSelectedGateway(selected);
    setDispositionsData(gatewayData, selected);
  };

  const handleSelectDate = async (date) => {
    setSelectedDate(date);
    const formattedDate = moment(date).format("DD-MM-YYYY");
    const res: any = await dispatch(
      gatewayStatusAction({
        date: formattedDate,
      })
    );
    setGatewayData(res);
    setDispositionsData(res, selectedGateway);
  };

  return (
    <Box className="my-2">
      <Box className="flex-between">
        <Box className="flex-align-center" gap={1}>
          <Typography className="font-20-grey my-2">Gateway Status</Typography>

          <Typography
            className="font-14-lightgrey"
            component="span"
            sx={commonStyles.textInfo}
          >
            Total calls{" "}
            <span className="font-20-700-lightgrey">{totalCalls || 0}</span>
          </Typography>
        </Box>
        <IconButton
          title="Refresh"
          onClick={applyRefresh}
          sx={{
            ...commonStyles.refreshButtonStyle,
            ...dashboardStyles.refreshAnimation(refreshLoading),
          }}
          disabled={refreshLoading}
        >
          <RefreshIcon className="refresh-icon" height={14} width={14} />
        </IconButton>
      </Box>

      <Box
        className="flex-align-center"
        sx={{
          flexWrap: { xs: "wrap", sm: "nowrap" },
          gap: 2,
          py: 2,
        }}
      >
        <Box className="flex-column" sx={{ flex: 1 }}>
          <Typography className="font-14-lightgrey">Call Statistics</Typography>
          <Typography className="font-16-600-grey">
            Disposition Summary
          </Typography>
        </Box>

        <Box className="flex-align-center" sx={{ gap: 2 }}>
          <Box>
            <Typography className="font-14-grey mb-1">Select Date</Typography>
            <Box className="date-container-2">
              <DatePicker
                selected={selectedDate}
                onChange={handleSelectDate}
                dateFormat="dd-MM-yyyy"
                placeholderText="Select a date"
                className="custom-date-input"
                maxDate={new Date()}
              />
              <CalendarTodayIcon fontSize="small" sx={{ color: "#666" }} />
            </Box>
          </Box>

          <Box sx={{ minWidth: { xs: "100%", sm: 200 } }}>
            <Typography className="font-14-grey mb-1">
              Select Gateway
            </Typography>

            <Select
              maxMenuHeight={180}
              className="basic-single mt-1 z-index-high"
              classNamePrefix="select"
              value={selectedGateway}
              options={constantData?.gateways || []}
              getOptionLabel={(e: any) => e.name}
              onChange={handleChange}
              placeholder="Select Gateway"
              isSearchable={false}
              styles={commonStyles.customSelect}
            />
          </Box>
        </Box>
      </Box>

      <Box>
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            borderRadius: 2,
            mx: "auto",
            border: "1px solid #e0e0e0",
            ...commonStyles.hideScroll,
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: "#f5f7fa" }}>
                <TableCell className="font-13-500">STATUS</TableCell>
                <TableCell className="font-13-500">DISPOSITION</TableCell>
                <TableCell className="font-13-500">CALLS</TableCell>
                <TableCell className="font-13-500">%</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dispositions?.length > 0 ? (
                dispositions?.map((row, idx) => (
                  <TableRow key={idx} sx={{ "& td": { borderBottom: "none" } }}>
                    <TableCell className="font-13-600-grey">
                      {row?.status}
                    </TableCell>
                    <TableCell className="font-13-600-grey">
                      {row?.disposition}
                    </TableCell>
                    <TableCell className="font-13-500">{row?.calls}</TableCell>
                    <TableCell className="font-13-500">
                      <Box className="flex-align-center" gap={1}>
                        <Typography
                          variant="body2"
                          fontWeight={500}
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {row?.percent?.toFixed(2)}%
                        </Typography>
                        <Box
                          sx={{
                            width: 60,
                            height: 8,
                            backgroundColor: row.bg,
                            borderRadius: 4,
                            overflow: "hidden",
                          }}
                        >
                          <Box
                            sx={{
                              width: `${row?.percent}%`,
                              height: "100%",
                              backgroundColor: row.color,
                              transition: "width 0.3s ease",
                            }}
                          />
                        </Box>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                    <Typography className="font-13-500">
                      No disposition data available for this gateway.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default GatewayDetails;
